import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Phone, PhoneForwarded, Clock, CheckCircle2, XCircle, Loader2, Mic, Send,
  ChevronDown, ChevronUp, PlayCircle, Trash2, AlertTriangle, Sparkles, ListChecks,
  Volume2, RefreshCw, UploadCloud,
} from 'lucide-react';
import { VoiceTest } from '../components/phone/VoiceTest';

const KIND_LABEL = { booking: 'Prenotazione', reschedule: 'Spostamento', cancel: 'Disdetta', callback: 'Richiamo' };

const StatusBlock = ({ status }) => {
  const s = status?.settings || {};
  const steps = status?.setup_steps || {};
  const pct = Math.min(100, Math.round((status?.minutes_used / Math.max(status?.minutes_cap || 1, 1)) * 100));
  const unlimited = !status?.minutes_cap;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
      <Card data-testid="phone-line-card">
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex items-center gap-2 mb-2">
            <Phone className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Stato della linea</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold font-outfit" data-testid="phone-line-state">
            {s.published && s.enabled ? 'Attiva' : 'Non attiva'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {s.ai_number ? `Numero AI: ${s.ai_number}` : 'Numero AI non ancora collegato'}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {s.forward_on_no_answer && <Badge variant="secondary" className="text-[10px]">Quando nessuno risponde</Badge>}
            {s.forward_outside_hours && <Badge variant="secondary" className="text-[10px]">Fuori orario</Badge>}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="phone-minutes-card">
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Minuti del mese</span>
          </div>
          <p className="text-xl sm:text-2xl font-bold font-outfit" data-testid="phone-minutes-used">
            {unlimited ? `${status?.minutes_used ?? 0} min` : `${status?.minutes_used ?? 0} / ${status?.minutes_cap ?? 0}`}
          </p>
          <div className="h-2 rounded-full bg-muted overflow-hidden mt-2">
            <div className={`h-full ${pct > 85 && !unlimited ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${unlimited ? 8 : pct}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {status?.calls_this_month ?? 0} chiamate gestite{unlimited ? ' · nessun tetto impostato' : ''}
          </p>
        </CardContent>
      </Card>

      <Card data-testid="phone-setup-card">
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Cosa manca</span>
          </div>
          <ul className="space-y-1.5 text-xs">
            {[
              ['Numero AI collegato', steps.number_linked],
              ['Deviazione impostata', steps.forwarding_set],
              ['Voce scelta', steps.voice_chosen],
              ['Voce collegata a ElevenLabs', steps.agent_live],
              ['Pubblicato', steps.published],
            ].map(([label, done]) => (
              <li key={label} className="flex items-center gap-2">
                {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-muted-foreground" />}
                <span className={done ? '' : 'text-muted-foreground'}>{label}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

const RequestsQueue = ({ requests, onAction, busyId }) => (
  <Card data-testid="phone-requests-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        Richieste da approvare
        {requests.length > 0 && <Badge className="ml-1">{requests.length}</Badge>}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2">
      {requests.length === 0 && <p className="text-sm text-muted-foreground">Nessuna richiesta in attesa.</p>}
      {requests.map(r => (
        <div key={r.id} className="border rounded-lg p-3 space-y-2" data-testid={`phone-request-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{KIND_LABEL[r.kind] || r.kind}</Badge>
            <span className="text-sm font-medium">{r.customer_name || 'Cliente'}</span>
            <span className="text-xs text-muted-foreground" data-testid={`request-phone-${r.id}`}>
              {r.details?.customer_phone || r.caller_number || 'numero non lasciato'}
            </span>
            {r.details?.customer_phone && r.details.customer_phone !== r.caller_number && (
              <Badge variant="secondary" className="text-[10px]">numero per la conferma</Badge>
            )}
          </div>
          <p className="text-sm">{r.details?.summary || r.details?.reason}</p>
          <p className="text-[11px] text-muted-foreground">
            Scade il {new Date(r.expires_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </p>
          <div className="flex gap-2">
            <Button size="sm" disabled={busyId === r.id} onClick={() => onAction(r.id, 'approve')} data-testid={`approve-${r.id}`}>
              {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Approva
            </Button>
            <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => onAction(r.id, 'reject')} data-testid={`reject-${r.id}`}>
              <XCircle className="w-3.5 h-3.5 mr-1" /> Rifiuta
            </Button>
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const CallRow = ({ call, api, authHeaders, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg p-3" data-testid={`phone-call-${call.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{call.customer_name || call.caller_number || 'Numero sconosciuto'}</span>
            <Badge variant="secondary" className="text-[10px]">{call.outcome}</Badge>
            <span className="text-xs text-muted-foreground">
              {Math.round((call.duration_seconds || 0) / 60)} min · {new Date(call.started_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <p className="text-sm mt-1">{call.summary}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          {call.conversation_id && (
            <Button variant="ghost" size="icon" title="Riascolta" data-testid={`play-${call.id}`}
              className="min-h-[44px] min-w-[44px]"
              onClick={async () => {
                try {
                  const res = await axios.get(`${api}/phone/calls/${call.id}/audio`,
                    { ...authHeaders(), responseType: 'blob' });
                  new Audio(URL.createObjectURL(res.data)).play();
                } catch { toast.error('Audio della chiamata non disponibile'); }
              }}>
              <PlayCircle className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => setOpen(o => !o)} data-testid={`transcript-${call.id}`}>
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(call.id)} data-testid={`delete-call-${call.id}`}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </div>
      {open && (
        <pre className="mt-2 text-xs whitespace-pre-wrap bg-muted/50 rounded-md p-2 max-h-64 overflow-auto" data-testid={`transcript-text-${call.id}`}>
          {call.transcript || 'Trascrizione non disponibile'}
        </pre>
      )}
    </div>
  );
};

const PhonePage = () => {
  const { getAuthHeaders, API } = useAuth();
  const [status, setStatus] = useState(null);
  const [requests, setRequests] = useState([]);
  const [calls, setCalls] = useState([]);
  const [voices, setVoices] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [provider, setProvider] = useState(null);
  const sessionRef = useRef(`test-${Date.now()}`);

  const load = useCallback(async () => {
    try {
      const [st, rq, cl, vc] = await Promise.all([
        axios.get(`${API}/phone/status`, getAuthHeaders()),
        axios.get(`${API}/phone/requests?status=pending`, getAuthHeaders()),
        axios.get(`${API}/phone/calls`, getAuthHeaders()),
        axios.get(`${API}/phone/voices`, getAuthHeaders()),
      ]);
      setStatus(st.data);
      setForm(st.data.settings);
      setRequests(rq.data.requests);
      setCalls(cl.data.calls);
      setVoices(vc.data.voices || []);
      axios.get(`${API}/phone/provider-check`, getAuthHeaders())
        .then(r => setProvider(r.data)).catch(() => setProvider(null));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento sezione Telefono');
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/phone/settings`, {
        ...form,
        blocked_numbers: typeof form.blocked_numbers === 'string'
          ? form.blocked_numbers.split(',').map(s => s.trim()).filter(Boolean)
          : form.blocked_numbers,
      }, getAuthHeaders());
      toast.success('Configurazione salvata');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const handleRequest = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/phone/requests/${id}/${action}`, {}, getAuthHeaders());
      toast.success(action === 'approve' ? 'Richiesta approvata, conferma inviata al cliente' : 'Richiesta rifiutata');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusyId(null);
    }
  };

  const deleteCall = async (id) => {
    try {
      await axios.delete(`${API}/phone/calls/${id}`, getAuthHeaders());
      setCalls(prev => prev.filter(c => c.id !== id));
    } catch { toast.error('Eliminazione non riuscita'); }
  };

  const syncCalls = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/phone/calls/sync`, {}, getAuthHeaders());
      setCalls(res.data.calls || []);
      toast.success(res.data.imported ? `${res.data.imported} nuove chiamate importate` : 'Registro già aggiornato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aggiornamento non riuscito');
    } finally {
      setSyncing(false);
    }
  };

  const publishAgent = async () => {
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/phone/agent/publish`, {}, getAuthHeaders());
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pubblicazione non riuscita', { duration: 9000 });
    } finally {
      setPublishing(false);
    }
  };

  const playPreview = async () => {
    if (!form?.voice_id) { toast.error('Scegli prima una voce'); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/phone/voice-preview`, { voice_id: form.voice_id },
        { ...getAuthHeaders(), responseType: 'blob' });
      await new Audio(URL.createObjectURL(res.data)).play();
    } catch (err) {
      const detail = err.response?.data instanceof Blob ? await err.response.data.text() : null;
      let msg = 'Anteprima voce non riuscita';
      try { msg = JSON.parse(detail).detail || msg; } catch { /* testo non JSON */ }
      toast.error(msg, { duration: 9000 });
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatInput('');
    setChat(prev => [...prev, { role: 'user', content: text }]);
    setChatBusy(true);
    try {
      const res = await axios.post(`${API}/phone/test-chat`, {
        session_id: sessionRef.current, message: text, caller_number: form?.test_caller || '+393331234567',
      }, getAuthHeaders());
      setChat(prev => [...prev, { role: 'assistant', content: res.data.reply, action: res.data.result?.action }]);
      if (['book', 'reschedule', 'cancel', 'callback'].includes(res.data.result?.action)) load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Prova non riuscita');
    } finally {
      setChatBusy(false);
    }
  };

  if (!status || !form) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="phone-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-outfit">Telefono</h1>
        <p className="text-sm md:text-base text-muted-foreground">Risponditore AI che risponde quando il salone non può</p>
      </div>

      {!status.voice_provider_ready && (
        <Card className="border-amber-500/40 bg-amber-500/5" data-testid="phone-keys-warning">
          <CardContent className="pt-4 text-sm">
            La voce al telefono richiede le chiavi di ElevenLabs e del numero telefonico. Nel frattempo puoi
            configurare tutto e <strong>provare l'assistente dal browser</strong>: usa gli stessi dati e crea
            richieste reali da approvare.
          </CardContent>
        </Card>
      )}

      {status.voice_provider_ready && provider && !provider.agents_ready && (
        <Card className="border-amber-500/40 bg-amber-500/5" data-testid="phone-permissions-warning">
          <CardContent className="pt-4 text-sm space-y-1">
            <p className="font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> La voce va sbloccata</p>
            <p className="text-muted-foreground">{provider.problem}</p>
            <p className="text-muted-foreground">
              L'assaggio della voce funziona già. Per la conversazione a voce servono i permessi
              <strong> Agents (lettura e scrittura)</strong> sulla chiave.
            </p>
          </CardContent>
        </Card>
      )}

      <StatusBlock status={status} />

      <Tabs defaultValue="overview">
        <TabsList className="w-full sm:w-auto flex justify-start gap-1 h-auto p-1 sm:inline-flex" data-testid="phone-tabs">
          <TabsTrigger value="overview" data-testid="phone-tab-overview">Panoramica</TabsTrigger>
          <TabsTrigger value="calls" data-testid="phone-tab-calls">Chiamate</TabsTrigger>
          <TabsTrigger value="config" data-testid="phone-tab-config">Configurazione</TabsTrigger>
          <TabsTrigger value="setup" data-testid="phone-tab-setup">Attivazione</TabsTrigger>
          <TabsTrigger value="test" data-testid="phone-tab-test">Prova</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <RequestsQueue requests={requests} onAction={handleRequest} busyId={busyId} />
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Ultime chiamate</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {calls.length === 0 && <p className="text-sm text-muted-foreground">Nessuna chiamata registrata.</p>}
              {calls.slice(0, 5).map(c => (
                <CallRow key={c.id} call={c} api={API} authHeaders={getAuthHeaders} onDelete={deleteCall} />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calls" className="mt-4">
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Registro chiamate</CardTitle>
              <Button variant="outline" size="sm" onClick={syncCalls} disabled={syncing}
                className="min-h-[44px]" data-testid="sync-calls-btn">
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Aggiorna
              </Button>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="phone-calls-list">
              {calls.length === 0 && <p className="text-sm text-muted-foreground">Il registro si popola con le chiamate gestite dall'assistente.</p>}
              {calls.map(c => <CallRow key={c.id} call={c} api={API} authHeaders={getAuthHeaders} onDelete={deleteCall} />)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="config" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Voce e stile</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Come si presenta</Label>
                  <Input value={form.agent_name || ''} onChange={e => setForm({ ...form, agent_name: e.target.value })} data-testid="agent-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Voce</Label>
                  <div className="flex gap-2">
                    <Select value={form.voice_id || ''} onValueChange={v => setForm({ ...form, voice_id: v })}>
                      <SelectTrigger data-testid="voice-select"><SelectValue placeholder="Scegli una voce" /></SelectTrigger>
                      <SelectContent>
                        {voices.map(v => <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={playPreview} title="Ascolta un assaggio"
                      disabled={previewing || !status.voice_provider_ready}
                      className="shrink-0 min-h-[44px] min-w-[44px]" data-testid="voice-preview-btn">
                      {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tono</Label>
                  <Select value={form.tone || 'formale'} onValueChange={v => setForm({ ...form, tone: v })}>
                    <SelectTrigger data-testid="tone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="formale">Formale (dà del Lei)</SelectItem>
                      <SelectItem value="informale">Informale (dà del tu)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tetto minuti al mese (0 = nessun limite)</Label>
                  <Input type="number" min="0" value={form.monthly_minutes_cap ?? 200}
                    onChange={e => setForm({ ...form, monthly_minutes_cap: parseInt(e.target.value) || 0 })} data-testid="minutes-cap-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Messaggio di benvenuto</Label>
                <Textarea rows={2} value={form.greeting || ''} placeholder="Lasciando vuoto viene usato un saluto con il nome del salone"
                  onChange={e => setForm({ ...form, greeting: e.target.value })} data-testid="greeting-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Da dire sempre</Label>
                  <Textarea rows={3} value={form.always_say || ''} placeholder="Es. il sabato serve una caparra"
                    onChange={e => setForm({ ...form, always_say: e.target.value })} data-testid="always-say-input" />
                </div>
                <div className="space-y-2">
                  <Label>Da non dire mai</Label>
                  <Textarea rows={3} value={form.never_say || ''} placeholder="Es. nessuna consulenza medica"
                    onChange={e => setForm({ ...form, never_say: e.target.value })} data-testid="never-say-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Quando risponde e limiti</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div><p className="text-sm font-medium">Quando nessuno risponde</p>
                  <p className="text-xs text-muted-foreground">Dopo {form.rings_before_forward || 4} squilli</p></div>
                <Switch checked={!!form.forward_on_no_answer} onCheckedChange={v => setForm({ ...form, forward_on_no_answer: v })} data-testid="forward-no-answer-switch" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div><p className="text-sm font-medium">Fuori orario e giorni di chiusura</p>
                  <p className="text-xs text-muted-foreground">Usa gli orari della scheda attività</p></div>
                <Switch checked={!!form.forward_outside_hours} onCheckedChange={v => setForm({ ...form, forward_outside_hours: v })} data-testid="forward-outside-switch" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Numero AI (quello su cui deviare)</Label>
                  <Input value={form.ai_number || ''} placeholder="+39..." onChange={e => setForm({ ...form, ai_number: e.target.value })} data-testid="ai-number-input" />
                </div>
                <div className="space-y-2">
                  <Label>Scadenza richieste (ore)</Label>
                  <Input type="number" min="1" value={form.approval_expiry_hours ?? 12}
                    onChange={e => setForm({ ...form, approval_expiry_hours: parseInt(e.target.value) || 12 })} data-testid="expiry-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Numeri bloccati (separati da virgola)</Label>
                <Input value={Array.isArray(form.blocked_numbers) ? form.blocked_numbers.join(', ') : (form.blocked_numbers || '')}
                  onChange={e => setForm({ ...form, blocked_numbers: e.target.value })} data-testid="blocked-numbers-input" />
              </div>
              <div className="flex items-center justify-between gap-3 border-t pt-4">
                <div><p className="text-sm font-medium">Risponditore pubblicato</p>
                  <p className="text-xs text-muted-foreground">Attiva la risposta automatica alle chiamate deviate</p></div>
                <Switch checked={!!(form.published && form.enabled)}
                  onCheckedChange={v => setForm({ ...form, published: v, enabled: v })} data-testid="publish-switch" />
              </div>
              <Button onClick={save} disabled={saving} className="w-full sm:w-auto min-h-[44px]" data-testid="save-settings-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salva configurazione
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Voce al telefono</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                Applica voce, saluto e istruzioni all'assistente vocale su ElevenLabs. Da rifare solo se vuoi
                forzare l'allineamento: il salvataggio della configurazione lo fa già da solo.
              </p>
              {status.agent_synced_at && (
                <p className="text-xs text-muted-foreground" data-testid="agent-synced-at">
                  Ultimo allineamento: {new Date(status.agent_synced_at).toLocaleString('it-IT')}
                </p>
              )}
              <Button variant="outline" onClick={publishAgent} disabled={publishing || !status.voice_provider_ready}
                className="min-h-[44px]" data-testid="publish-agent-btn">
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
                Allinea la voce
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="setup" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Attivazione in 4 passaggi</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              {[
                ['1. Collega un numero dedicato', 'Acquista un numero dall\'operatore telefonico e inseriscilo in Configurazione → "Numero AI". Il numero storico del salone resta quello che i clienti chiamano: per i numeri fissi italiani servono P.IVA, documento del titolare e prova di indirizzo, con verifica in 1-3 giorni.'],
                ['2. Scegli quando risponde', 'In Configurazione attiva "quando nessuno risponde" e/o "fuori orario".'],
                ['3. Attiva la deviazione dal telefono del salone', 'Digita i codici qui sotto dal telefono del salone (o imposta la deviazione dal pannello del tuo operatore).'],
                ['4. Prova e pubblica', 'Usa la scheda "Prova" per sentire come risponde, poi attiva "Risponditore pubblicato".'],
              ].map(([title, text]) => (
                <div key={title} className="border rounded-lg p-3">
                  <p className="font-medium">{title}</p>
                  <p className="text-muted-foreground mt-1">{text}</p>
                </div>
              ))}
              <div className="border rounded-lg p-3 space-y-1" data-testid="forwarding-codes">
                <p className="font-medium flex items-center gap-2"><PhoneForwarded className="w-4 h-4 text-primary" /> Codici di deviazione</p>
                {Object.entries({
                  'Se nessuno risponde': status.forwarding_codes.no_answer,
                  'Se occupato': status.forwarding_codes.busy,
                  'Se non raggiungibile': status.forwarding_codes.unreachable,
                  'Disattiva tutte le deviazioni': status.forwarding_codes.disable_all,
                }).map(([label, code]) => (
                  <p key={label} className="text-muted-foreground">{label}: <code className="text-foreground">{code}</code></p>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Prova l'assistente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Scrivi come se stessi telefonando: l'assistente usa listino, orari e slot reali e le richieste
                create finiscono nella coda da approvare. Non consuma minuti telefonici.
              </p>
              <div className="border rounded-lg p-3 space-y-2 max-h-[360px] overflow-auto bg-muted/30" data-testid="test-chat-log">
                {chat.length === 0 && <p className="text-sm text-muted-foreground">Prova con: "Buongiorno, vorrei un taglio giovedì pomeriggio"</p>}
                {chat.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-background border'}`}>
                      {m.content}
                      {m.action && m.action !== 'none' && (
                        <Badge variant="secondary" className="ml-2 text-[10px] align-middle">{m.action}</Badge>
                      )}
                    </div>
                  </div>
                ))}
                {chatBusy && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              </div>
              <div className="flex gap-2">
                <Input value={chatInput} onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendTest(); }}
                  placeholder="Scrivi come al telefono..." data-testid="test-chat-input" />
                <Button onClick={sendTest} disabled={chatBusy} className="shrink-0" data-testid="test-chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Mic className="w-3 h-3" /> Qui sotto puoi provare la stessa cosa parlando al microfono.
              </p>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Mic className="w-4 h-4 text-primary" /> Prova a voce</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Parla al microfono come se stessi telefonando: senti la voce vera dell'assistente e le
                richieste che crea finiscono nella coda da approvare.
              </p>
              {status.voice_provider_ready ? (
                <VoiceTest api={API} authHeaders={getAuthHeaders} onFinished={load} />
              ) : (
                <p className="text-sm text-muted-foreground">Serve la chiave ElevenLabs per parlare al microfono.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PhonePage;
